import mustache from 'mustache';
import debounce from 'lodash/debounce';
import {baseUrl} from './baseurl';
import LoadingIcon from '../generic/loading-icon';

/**
 * Offers and events page client side logic
 */
class HeroBannerFilters {

  /**
   * @type {object} - Stored loaded card templates. Key is template type and value is template data.
   */
  #cardTemplates = {};

  /**
   * Maximum pages load with infinite scroll
   * until the "load more" button will set visible
   *
   * @type {number}
   * @private
   */
  #maxInfiniteLoadPages = 2;

  /**
   * @constructor
   * @param {HTMLElement} element - Main element of this view
   */
  constructor(element) {
    this._element = element;

    // Due moving filters outside of main element,
    // get filters element to its own member variable
    this._filtersElement = document.querySelector('.hero-banner-filters__filters');

    this._elements();
    this._bind();
    this._page = 0;//Paging start from zero
    this._singleBrand = this._element.dataset.singlebrand === 'true';

    //This is just to show correct values in the mobile view button text
    this._counts = {
      total: this._element.dataset.total,
      available: this._element.dataset.available
    }

    //Debounced version of load method for preventing unnecessary fetch calls.
    this.debouncedLoad = debounce(this.#load, 500, { trailing: true, leading: false });

    this._loadMoreBtn.classList.toggle('hidden', true);

    this.#initObserver();
    this.#startInterSectionObservation();
  }

  /**
   * Set a ui state for loading spinner element.
   *
   * @param {boolean} value - If true is passed container will be fade a bit or false is passed container has none opacity.
   */
  set isLoading(value) {
    this._resultElement.classList.toggle('hero-banner-filters--loading', value);
    LoadingIcon.instances['model-page-loading-icon'].show = value;
  }

  /**
   * All elements accessed in this view
   */
  _elements() {
    this._loadMoreBtn = this._element.querySelector('.btn-load-more');
    this._categoryInputElements = this._filtersElement.querySelectorAll('input[name="type"]');
    this._brandInputElements = this._filtersElement.querySelectorAll('input[name="brand"]');
    this._labelCategoryElements = this._filtersElement.querySelectorAll('.hero-banner-filters__category-filter');
    this._labelBrandElements = this._filtersElement.querySelectorAll('.hero-banner-filters__brand-filter');
    this._resultElement = this._element.querySelector('.offers-grid');
    this._singleLift = this._element.querySelectorAll('.single-lift a');
    this._amountText = this._element.querySelector('.amount');
    this._mobileCloseFilters = this._filtersElement.querySelector('.hero-banner-filters__close-mobile-filters');
    this._mobileShowResults = this._filtersElement.querySelector('.hero-banner-filters__mobile-show-results');
    this._clearFiltersBtn = this._filtersElement.querySelector('.hero-banner-filters__mobile-clear-filters');
    this._totalLiftCount = this._element.querySelector('.hero-banner-filters__caption span');
    this._intersectionObservableElement = document.querySelector('.btn-load-more-container');
    this._noMoreSearchResult = this._element.querySelector('.no-more-search-result');

    // Ouside of filters element due styling (paddings), search it from document
    this._mobileFiltersButton = document.querySelector('.hero-banner-filters__mobile-filters-toggle .hedin-btn');
  }

  /**
   * Bind event listeners
   */
  _bind() {
    this._loadMoreBtn.addEventListener('click', this._onLoadMoreBtnClicked.bind(this) );

    this._categoryInputElements?.forEach( element => element.addEventListener('change', this.#onCategoryInputClicked.bind(this, element)));
    this._brandInputElements?.forEach( element => element.addEventListener('change', this.#onBrandInputClicked.bind(this, element)));

    this._clearFiltersBtn.addEventListener('click', this.#clearFilters.bind(this));

    [ this._mobileFiltersButton, this._mobileCloseFilters, this._mobileShowResults ].forEach(e => {
      e.addEventListener('click', this.#mobileFiltersToggle.bind(this));
    })
  }

  /**
   * Handle load more button clicks
   * Fetches more lifts if exists.
   *
   * @async
   */
  async _onLoadMoreBtnClicked() {

    this.#showFetchMoreBtn(false);
    this.isLoading = true;
    const formData = new URLSearchParams();

    this._page = this._page + 1;
    formData.append('page', this._page);

    const responseJson = await this.#fetchSearchResult(formData);
    const templateData = await this.#fetchCardTemplate(responseJson.templateType);

    const htmlRendered = this.#getRenderedHtml(responseJson.cardData, templateData);
    this._resultElement.innerHTML += htmlRendered;
  }

  /**
   * Render lifts HTML with given lift array of object
   *
   * @param {object[]} lifts - Lifts array got from ajax-endpoint
   * @param {object} templateInfo - Template info that contains lifts template data
   * @returns {string} - Mustache rendered HTML
   */
  #getRenderedHtml(lifts, templateInfo) {
    return lifts.map(lift => {
      return mustache.render( templateInfo.template, {
        ...lift,
        cardCustomClasses: templateInfo.cardCustomClasses
      }, templateInfo.partials );
    }).join('');
  }

  /**
   * Toggle mobile filters on / off when mobile
   * filters button is pressed
   */
  #mobileFiltersToggle() {
    this._filtersElement.classList.toggle('hero-banner-filters--in');
  }

  /**
   * Makes a article search request
   * @async
   * @param {URLSearchParams} formData get parameters for a request
   * @return {object} responseJson object
   */
  async #fetchSearchResult( formData ) {

    this.#stopIntersectionObservation();

    try {
      //Each search has it's own AbortController instance so they don't mixup between request.
      this._controller = new window.AbortController();

      formData.append('category', this.#getSlug());
      formData.append('currentBrand', document.body.dataset.brand);

      const response = await fetch( this._filtersElement.dataset.searchUrl + '?' + formData.toString() , {
        method: 'GET'
      });

      const responseJson = await response.json();
      //this._loadMoreBtn.classList.toggle('hidden', responseJson.counts.available <= 0);
      this._amountText.innerText = responseJson.counts.available;
      this._counts = responseJson.counts;

      this.isLoading = false;

      //Operation performed after each succesfull search is done there..
      this.#afterSearch(responseJson);

      return responseJson;
    } catch( error ) {
      if (error.name !== 'AbortError') {
        console.warn('Unhandled error:', error);
      }
    }

    this.isLoading = false;
  }

  /**
   * Fetch car card via Stage ajax interface
   * @async
   * @param {string} cardType - Card type to fetch
   * @return {Promise} resolved after successful call
   */
  async #fetchCardTemplate( cardType ) {
    if( !this.#cardTemplates[cardType] ) {
      const response = await fetch('/stage-ajax/card-template?cardtype=' + cardType );
      this.#cardTemplates[cardType] = response.json();
    }

    return this.#cardTemplates[cardType];
  }


  /**
   * Clear all selected filters
   */
  #clearFilters() {
    this._brandInputElements?.forEach(input => {
      input.checked = false;
      input?.closest('label')?.classList?.remove('hedin-checkbox__checkbox--checked');
    });

    // default radio group radio element check
    this._filtersElement.querySelector('.default-radio').checked = true;

    this.#load();
  }

  /**
   * Initial articles from the server, there is debounced version of this method reference stored to
   * this.debouncedLoad variable. Use it as possible.. to prevent unnecessary request spam.
   * Makes a request and append result to dom.
   * @async
   */
  async #load() {
    this.isLoading = true;

    this.#updateHistoryState();
    this._page = 0;
    this.#abortSearch();
    const formData = new URLSearchParams();

    const responseJson = await this.#fetchSearchResult(formData);
    const templateData = await this.#fetchCardTemplate(responseJson.templateType);

    const htmlRendered = this.#getRenderedHtml(responseJson.cardData, templateData);
    this._resultElement.innerHTML = htmlRendered;

    this._totalLiftCount.innerText = responseJson.counts.total;
  }

  /**
   * Called when type element is clicked
   * @param {HTMLElement} element input element that was clicked
   * @param {Event} event click event
   */
  async #onCategoryInputClicked() {
    this.#stopIntersectionObservation();
    this.#abortSearch();
    this.debouncedLoad();
  }

   /**
   * Called after search
   * @private
   * @param {object} responseJSON Model page search response object
   */
   #afterSearch(responseJSON) {

    if( responseJSON.counts.available <= 0 ) {
      this.#showNoMoreSearchResult(true);
      this.#showFetchMoreBtn(false);
      this.#stopIntersectionObservation();
    } else {
      this.#showNoMoreSearchResult(false);

      if( this._page >= 2 ) {
        this.#showFetchMoreBtn(true);
        this.#stopIntersectionObservation();
      } else {
        this.#startInterSectionObservation();
      }
    }
  }


   /**
   * Called when brand element is clicked
   * @param {HTMLElement} element input element that was clicked
   * @param {Event} event click event
   */
   async #onBrandInputClicked(element, event) {

     this.#stopIntersectionObservation();

     if( !this._singleBrand ) {
       this.#updateHistoryState();
       this.#abortSearch();
       this.debouncedLoad();
     } else {
       event.preventDefault();
     }
   }

   /**
 * Show or hide no more search result text bottom of the search results
 * @param {boolean} show true to show false to hide
 */
  #showFetchMoreBtn( show ) {
     this._loadMoreBtn.classList.toggle('hidden', !show );
   }


  #showNoMoreSearchResult( show ) {
    this._noMoreSearchResult.classList.toggle('hidden', !show );
  }

  /**
   * Return request only parameter, a slug is combination of selected brands and selected filter types.
   *
   * @return {string} something like 'bmw-nissan-tapahtumat'
   */
  #getSlug() {
    const types = Array.from( this._categoryInputElements ).filter( element => element.checked ).map( element => element.value ).join('-');
    const brands = Array.from( this._brandInputElements ).filter( element => element.checked ).map( element => element.value ).join('-');
    return [ types, brands ].filter(Boolean).join('-');
  }

  /**
   * Update page URL-address based on what user select from filters
   * @param {string} selectedValue selected value from filters
   */
  #updateHistoryState() {
    window.history.pushState( null, null, this.#url() + `${this.#getSlug()}` );
  }

  /**
   * Called on fetch request is aborted
   */
  #abortSearch() {
    if (this._controller) {
      this._controller.abort();
    }
  }

  /**
   * Base url address for the fetch request, this need to be created dynamically
   * that history status can be refreshed in all brand pages right way.
   *
   * @return {string} base url address
   */
  #url() {
    const splitPoint = this._filtersElement.dataset.splitpoint;

    if( splitPoint ) {
      return splitPoint === 'uutisvirta' ? baseUrl(window.location.href, 'ajankohtaista') : baseUrl(window.location.href, splitPoint);
    } else {
      return window.location.href;
    }
  }

  /**
   * Observer for load more button when user
   * scrolls button to viewport, execute load more
   */
  #initObserver() {
    this._observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (!this.isLoading && entry.isIntersecting && this._page < this.#maxInfiniteLoadPages) {
          this._loadMoreBtn.click();
        }
      });
    }, { threshold: 1 });


  }

  /**
   * Stop intersection observing, don't load more search result automatically when scrolling down.
   * @private
   */
  #stopIntersectionObservation() {
    this._observer.unobserve(this._intersectionObservableElement);
  }

  /**
   * Start intersection observing
   * @private
   */
  #startInterSectionObservation() {
    this._observer.observe(this._intersectionObservableElement);
  }

  /**
   * Initializes view in the master.js
   *
   * @return {HeroBannerFilters|undefined}  if element with .offer-and-event class is found from dom OfferAndEvents class instance is returned.
   */
  static init() {
    const element = document.querySelector('.offers-and-events,.news-stream');
    if( element ) {
      return new HeroBannerFilters(element);
    }
  }
}

export default HeroBannerFilters
